/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import * as _ from 'lodash';

import {OBJECT_TYPE} from '../../common/constants/object-type';
import {buildLastViewedEntries} from './simple-last-viewed.util';
import template from './version-history-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {VersionHistoryResource} from '../../../components/api/resources/resource/version-history';
import {ObjectDiffService} from '../../common/util/object-diff.service';
import {createBufferedFlag, readBufferedFlag} from '../../common/util/template-state';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';
import {NavigationHistoryService} from '../../../components/util/navigation-history.service';

@Component({
  standalone: false,
  selector: 'adn-version-history-page',
  template
})
export class VersionHistoryPageComponent implements OnInit, OnDestroy {
  lastViewed: any[] = [];
  lastViewedObject: any;
  selectFrom = 'LAST_VIEWED';

  loaded = true;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  pageSize = 30;
  currentPage = 1;
  pageQuery: any = {$resolved: true};
  versions: any[] = [];
  items: any[] = [];
  objectId: string;
  objectType: any;
  objectTypes = OBJECT_TYPE;
  readonly objectTypeKeys = Object.keys(OBJECT_TYPE);

  masquerade = false;
  totalCount: number;

  constructor(
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(ObjectDiffService) private readonly ObjectDiff: any,
    @Inject(VersionHistoryResource) private readonly versionHistoryResource: any,
    private readonly cdr: ChangeDetectorRef,
    navigationHistory: NavigationHistoryService
  ) {
    this.lastViewed = buildLastViewedEntries(this.localUserProfile, this.LocalNetworkProfile);
    this.lastViewedObject = _.get(this.lastViewed, [0]);
    this.objectId = navigationHistory.previousParams.id;
    this.objectType = _.find(_.values(OBJECT_TYPE), (ot) => ot.stateName === navigationHistory.previousState?.name);
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  ngOnInit(): void {
    this.showHistory();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  showHistory(resetPage?: boolean): void {
    const objType = this.selectFrom === 'LAST_VIEWED'
      ? _.get(this, ['lastViewedObject', 'objType', 'searchObject']) || _.get(this, ['lastViewedObject', 'objType', 'type'])
      : _.get(this, ['objectType', 'searchObject']) || _.get(this, ['objectType', 'type']);
    const objId = this.selectFrom === 'LAST_VIEWED' ? _.get(this, ['lastViewedObject', 'id']) : this.objectId;

    if (objId && objType) {
      if (resetPage) {
        this.currentPage = 1;
      }
      this.loaded = false;
      this.masquerade = false;
      this.pageQuery = {$resolved: false};
      this.versionHistoryResource.get(objId, objType, this.currentPage, this.pageSize).then((data: any) => {
        this.parseVersionHistory(data);
        this.loaded = true;
        this.totalCount = data.totalCount;
        this.pageQuery.$resolved = true;
      });
    }
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.showHistory(false);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.currentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.pageSize * (this.currentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.pageSize * this.currentPage, this.totalCount || 0);
  }

  clipboardText(value: any): string {
    if (value === undefined || value === null) {
      return '';
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
  }

  private parseVersionHistory(data: any): void {
    let prevVersion: any = {};
    this.versions = _.reverse(_.map(_.reverse(data.results), (result: any) => {
      let currentVersion: any;
      try {
        currentVersion = JSON.parse(result.json);
      } catch {
        prevVersion = {};
        return {
          user: result.user || null,
          masqueraderUser: result.masqueraderUser || null,
          timestamp: result.timestamp,
          diff: '<p>undefined</p>',
          json: '<p>undefined</p>'
        };
      }

      if (_.isEmpty(prevVersion) && result.previousJson) {
        prevVersion = JSON.parse(result.previousJson);
      }
      const diff = this.ObjectDiff.diff(prevVersion, currentVersion);
      const versionData = {
        user: result.user || null,
        masqueraderUser: result.masqueraderUser || null,
        timestamp: result.timestamp,
        diff: this.ObjectDiff.toJsonDiffView(diff),
        json: this.ObjectDiff.toJsonView(diff),
        diffRaw: _.filter(_.map(_.get(diff, 'value'), (val: any, key: string) => {
          if (_.isPlainObject(val)) {
            val.field = key;
          }
          return val;
        }), (val: any) => _.get(val, 'changed') !== 'equal'),
        jsonRaw: currentVersion
      };

      if (!this.masquerade && result.masqueraderUser) {
        this.masquerade = true;
      }

      prevVersion = currentVersion;
      return versionData;
    }));
    this.items = this.versions;
  }
}
